import { useState, Fragment, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Save, X, Plus, Trash2 } from 'lucide-react';
import {
  Filter,
  FilterType,
  LogicOperator,
  Condition,
  ConditionGroup,
  FILTER_TYPES,
  generateConditionId,
  generateGroupId,
  getFilterTypeMetadata,
  getOperatorMetadata,
} from '@/types/filter';
import { ConditionBuilder } from './ConditionBuilder';
import { ConditionGroupBuilder } from './ConditionGroupBuilder';

interface FilterEditorProps {
  filter: Partial<Filter>;
  onSave: (filter: Partial<Filter>) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isSaving?: boolean;
}

export function FilterEditor({
  filter,
  onSave,
  onCancel,
  onDelete,
  isSaving = false,
}: FilterEditorProps) {
  const isNewFilter = !filter.id;
  const filterNameInputRef = useRef<HTMLInputElement>(null);

  const [editedFilter, setEditedFilter] = useState<Partial<Filter>>(() => {
    // Initialize with first condition if it's a new filter with no conditions
    const conditions = isNewFilter && (!filter.conditions || filter.conditions.length === 0)
      ? [{
          id: generateConditionId(),
          field: 'from_email', // Default to from_email field
          operator: 'contains',
          value: '',
          caseSensitive: false,
        }]
      : (filter.conditions || []);

    return {
      ...filter,
      name: filter.name || 'Filter 1',
      logicOperator: filter.logicOperator || 'AND',
      conditions: conditions,
      conditionGroups: filter.conditionGroups || [],
      optional: filter.optional ?? false,
    };
  });

  // Focus filter name input when creating a new filter
  useEffect(() => {
    if (isNewFilter && filterNameInputRef.current) {
      filterNameInputRef.current.focus();
    }
  }, [isNewFilter]);

  const handleAddCondition = () => {
    const newCondition: Condition = {
      id: generateConditionId(),
      field: 'from_email', // Default to from_email field
      operator: 'contains',
      value: '',
      caseSensitive: false,
    };

    setEditedFilter({
      ...editedFilter,
      conditions: [...(editedFilter.conditions || []), newCondition],
    });
  };

  const handleUpdateCondition = (index: number, condition: Condition) => {
    const newConditions = [...(editedFilter.conditions || [])];
    newConditions[index] = condition;
    setEditedFilter({
      ...editedFilter,
      conditions: newConditions,
    });
  };

  const handleRemoveCondition = (index: number) => {
    setEditedFilter({
      ...editedFilter,
      conditions: (editedFilter.conditions || []).filter((_, i) => i !== index),
    });
  };

  const handleDuplicateCondition = (index: number) => {
    const conditionToDuplicate = (editedFilter.conditions || [])[index];
    if (!conditionToDuplicate) return;

    // Create a new condition with the same properties but a new ID
    const duplicatedCondition: Condition = {
      ...conditionToDuplicate,
      id: generateConditionId(),
    };

    // Insert the duplicated condition right after the current one
    const newConditions = [...(editedFilter.conditions || [])];
    newConditions.splice(index + 1, 0, duplicatedCondition);

    setEditedFilter({
      ...editedFilter,
      conditions: newConditions,
    });

    // Focus the value field of the duplicated condition after DOM update
    const operatorMetadata = getOperatorMetadata(duplicatedCondition.operator);
    if (operatorMetadata.requiresValue) {
      setTimeout(() => {
        const valueField = document.getElementById(`value-${duplicatedCondition.id}`);
        if (valueField) {
          valueField.focus();
        }
      }, 0);
    }
  };

  const handleAddConditionGroup = () => {
    const newGroup: ConditionGroup = {
      id: generateGroupId(),
      logicOperator: 'AND',
      conditions: [{
        id: generateConditionId(),
        field: 'from_email', // Default to from_email field
        operator: 'contains',
        value: '',
        caseSensitive: false,
      }],
    };

    setEditedFilter({
      ...editedFilter,
      conditionGroups: [...(editedFilter.conditionGroups || []), newGroup],
    });
  };

  const handleUpdateConditionGroup = (index: number, group: ConditionGroup) => {
    const newGroups = [...(editedFilter.conditionGroups || [])];
    newGroups[index] = group;
    setEditedFilter({
      ...editedFilter,
      conditionGroups: newGroups,
    });
  };

  const handleRemoveConditionGroup = (index: number) => {
    setEditedFilter({
      ...editedFilter,
      conditionGroups: (editedFilter.conditionGroups || []).filter((_, i) => i !== index),
    });
  };

  const handleSave = () => {
    onSave(editedFilter);
  };

  const hasConditions = (editedFilter.conditions?.length || 0) > 0;
  const hasConditionGroups = (editedFilter.conditionGroups?.length || 0) > 0;

  // Check if condition groups have at least one condition
  const hasValidConditionGroups = hasConditionGroups &&
    (editedFilter.conditionGroups || []).some(group => (group.conditions?.length || 0) > 0);

  // Can only save if there's at least one condition or one valid condition group
  const canSave = hasConditions || hasValidConditionGroups;

  return (
    <Card className="bg-slate-50 dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-600 overflow-hidden rounded-lg">
      {/* Top Container - Filter Name */}
      <div className="p-6 bg-slate-100 dark:bg-slate-800 border-b border-gray-300 dark:border-gray-600">
        <Input
          ref={filterNameInputRef}
          id="filter-name"
          type="text"
          value={editedFilter.name}
          onChange={(e) =>
            setEditedFilter({ ...editedFilter, name: e.target.value })
          }
          placeholder="Enter filter name"
          tabIndex={1}
        />
      </div>

      {/* Main Content */}
      <div className="p-6 space-y-6">
        {/* Top-level Logic Operator */}
        <div className="py-4">
          <Select
            value={editedFilter.logicOperator}
            onValueChange={(value) =>
              setEditedFilter({
                ...editedFilter,
                logicOperator: value as LogicOperator,
              })
            }
          >
            <SelectTrigger id="logic-operator" className="w-full bg-white dark:bg-gray-900" tabIndex={2}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="AND">ALL conditions must be true</SelectItem>
              <SelectItem value="OR">ANY conditions can be true</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div>
          {/* Top-level Conditions */}
          {hasConditions && (
            <>
              <div className="space-y-4 mb-6">
                {(editedFilter.conditions || []).map((condition, index) => (
                  <Fragment key={condition.id}>
                    <ConditionBuilder
                      condition={condition}
                      onChange={(updated) => handleUpdateCondition(index, updated)}
                      onRemove={() => handleRemoveCondition(index)}
                      onDuplicate={() => handleDuplicateCondition(index)}
                      tabIndexStart={index === 0 ? 3 : undefined}
                    />
                    {index < (editedFilter.conditions?.length || 0) - 1 && (
                      <div className="flex justify-center py-2">
                        <span className="text-xs font-normal px-2 py-0.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-blue-600">
                          {editedFilter.logicOperator === 'AND' ? 'AND' : 'OR'}
                        </span>
                      </div>
                    )}
                  </Fragment>
                ))}
              </div>
              {/* Show AND/OR between conditions and condition groups if both exist */}
              {hasConditionGroups && (
                <div className="flex justify-center py-2 mb-6">
                  <span className="text-xs font-normal px-2 py-0.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-blue-600">
                    {editedFilter.logicOperator === 'AND' ? 'AND' : 'OR'}
                  </span>
                </div>
              )}
            </>
          )}

          {/* Condition Groups */}
          {hasConditionGroups && (
            <div className="space-y-5 mb-6">
              {(editedFilter.conditionGroups || []).map((group, index) => (
                <Fragment key={group.id}>
                  {index > 0 && (
                    <div className="flex justify-center py-2">
                      <span className="text-xs font-normal px-2 py-0.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-full text-blue-600">
                        {editedFilter.logicOperator === 'AND' ? 'AND' : 'OR'}
                      </span>
                    </div>
                  )}
                  <ConditionGroupBuilder
                    group={group}
                    onChange={(updated) => handleUpdateConditionGroup(index, updated)}
                    onRemove={() => handleRemoveConditionGroup(index)}
                  />
                </Fragment>
              ))}
            </div>
          )}

          {!hasConditions && !hasConditionGroups && (
            <>
              <div className="flex items-center justify-center py-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg bg-slate-50 dark:bg-gray-900">
                <p className="text-sm text-slate-500 dark:text-gray-400">
                  No conditions defined yet. Add conditions or condition groups to configure this filter.
                </p>
              </div>

              {/* Add Condition and Add Condition Group Buttons */}
              <div className="flex gap-2 justify-center py-6">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleAddCondition}
                  className="bg-blue-100"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Condition
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={handleAddConditionGroup}
                  className="bg-blue-100"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Condition Group
                </Button>
              </div>
            </>
          )}

          {/* Add Condition and Add Condition Group Buttons - Show when conditions exist */}
          {(hasConditions || hasConditionGroups) && (
            <div className="flex gap-2 justify-center py-6">
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleAddCondition}
                className="bg-blue-100"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Condition
              </Button>
              <Button
                type="button"
                variant="secondary"
                size="sm"
                onClick={handleAddConditionGroup}
                className="bg-blue-100"
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Condition Group
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Bottom Container - Action Buttons */}
      <div className="p-6 bg-slate-100 dark:bg-slate-800 border-t border-gray-300 dark:border-gray-600 flex flex-wrap gap-4 items-center justify-between">
        <div className="flex items-center gap-4">
          {onDelete && filter.id && (
            <Button
              type="button"
              variant="ghost"
              onClick={onDelete}
              disabled={isSaving}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300"
            >
              <Trash2 className="h-4 w-4 mr-1" />
              Delete
            </Button>
          )}

          {/* Optional Toggle */}
          <div className="flex items-center gap-2">
            <Switch
              id="optional-toggle"
              checked={editedFilter.optional ?? false}
              onCheckedChange={(checked) =>
                setEditedFilter({ ...editedFilter, optional: checked })
              }
              disabled={isSaving}
            />
            <Label
              htmlFor="optional-toggle"
              className="text-sm font-medium cursor-pointer"
            >
              Optional
            </Label>
          </div>
        </div>

        <div className="flex gap-2">
          <Button
            onClick={handleSave}
            disabled={isSaving || !editedFilter.name || !canSave}
          >
            <Save className="h-4 w-4 mr-1" />
            {isSaving ? 'Saving...' : 'Save Filter'}
          </Button>
          <Button
            type="button"
            variant="ghost"
            onClick={onCancel}
            disabled={isSaving}
          >
            <X className="h-4 w-4 mr-1" />
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}
